import { Clipboard } from '@angular/cdk/clipboard';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BridgeDeviceActionsService,
  EditBridgeSettingsDialogComponent,
  InformationBoxComponent,
  type IEditBridgeSettingsDialogData,
  type IEditBridgeSettingsDialogResult,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  LoaderButtonComponent,
  NgSharedModule,
} from '@principle-theorem/ng-shared';
import {
  BridgeVersion,
  CloudToBridgeCommand,
  type IBaseCommand,
  type IDeviceCommand,
} from '@principle-theorem/principle-bridge-core';
import {
  BridgeDevice,
  BridgeLogFreshness,
  buildBridgeDiagnosticReport,
  Practice,
  type IBridgeDeviceDiagnosticStatus,
} from '@principle-theorem/principle-core';
import {
  BridgeDeviceStatus,
  type IBridgeDevice,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  Firestore,
  serialise,
  type IAttachment,
  type WithRef,
} from '@principle-theorem/shared';
import { saveAs } from 'file-saver';
import { push, set } from 'firebase/database';
import { combineLatest, of, type Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ViewBridgeLogsDialogComponent,
  type IViewBridgeLogsDialogData,
} from '../view-bridge-logs-dialog/view-bridge-logs-dialog.component';

interface IDeviceWithFiles {
  device: WithRef<IBridgeDevice>;
  logFiles: WithRef<IAttachment>[];
}

@Component({
  selector: 'pt-bridge-diagnostic-status',
  templateUrl: './diagnostic-status.component.html',
  styleUrls: ['./diagnostic-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSharedModule,
    InformationBoxComponent,
    LoaderButtonComponent,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
  ],
})
export class BridgeDiagnosticStatusComponent {
  private _clipboard = inject(Clipboard);
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _bridgeActions = inject(BridgeDeviceActionsService);
  private _practice$!: Observable<WithRef<IPractice>>;

  practice = input.required<WithRef<IPractice>>();

  readonly displayedColumns = [
    'status',
    'name',
    'version',
    'logs',
    'latestLog',
    'actions',
  ];
  readonly BridgeLogFreshness = BridgeLogFreshness;
  readonly BridgeDeviceStatus = BridgeDeviceStatus;

  loading = signal(true);
  error = signal<string | undefined>(undefined);
  successMessage = signal<string | undefined>(undefined);
  devices = signal<IBridgeDeviceDiagnosticStatus[]>([]);
  requestingLogs = signal<Set<string>>(new Set());
  refreshingAll = signal(false);
  downloading = signal(false);

  constructor() {
    this._practice$ = toObservable(this.practice);
    this._observeDeviceStatuses();
  }

  getStatusIcon(status: BridgeDeviceStatus): string {
    return status === BridgeDeviceStatus.Active ? 'check_circle' : 'cancel';
  }

  getStatusColour(status: BridgeDeviceStatus): string {
    return status === BridgeDeviceStatus.Active
      ? 'text-green-600'
      : 'text-slate-300';
  }

  getStatusLabel(status: BridgeDeviceStatus): string {
    return status === BridgeDeviceStatus.Active ? 'Active' : 'Inactive';
  }

  getLogIcon(freshness: BridgeLogFreshness): string {
    return freshness === BridgeLogFreshness.None
      ? 'hide_source'
      : 'description';
  }

  getLogColour(freshness: BridgeLogFreshness): string {
    switch (freshness) {
      case BridgeLogFreshness.Fresh:
        return 'text-green-600';
      case BridgeLogFreshness.Stale:
        return 'text-orange-500';
      case BridgeLogFreshness.None:
        return 'text-slate-300';
      default:
        return 'text-slate-300';
    }
  }

  getLogTooltip(device: IBridgeDeviceDiagnosticStatus): string {
    switch (device.logFreshness) {
      case BridgeLogFreshness.Fresh:
        return `Logs from today (${device.logCount} file${
          device.logCount === 1 ? '' : 's'
        })`;
      case BridgeLogFreshness.Stale:
        return `Stale logs (${device.logCount} file${
          device.logCount === 1 ? '' : 's'
        })`;
      case BridgeLogFreshness.None:
        return 'No logs available';
      default:
        return '';
    }
  }

  async requestAllLogs(): Promise<void> {
    this.refreshingAll.set(true);
    this._clearMessages();

    try {
      const devicesWithId = this.devices().filter((d) => d.deviceId);
      if (devicesWithId.length === 0) {
        this.error.set('No devices with a device ID to request logs from');
        return;
      }

      const practice = this.practice();
      const brandRef = Practice.brandDoc(practice);
      const organisationId = Firestore.getParentDocRef(brandRef).id;
      const command = this._buildGetLogsCommand();

      await Promise.all(
        devicesWithId.map((device) =>
          this._sendCommand(command, device, practice, brandRef, organisationId)
        )
      );

      this.successMessage.set(
        `Logs requested from ${devicesWithId.length} device${
          devicesWithId.length === 1 ? '' : 's'
        }`
      );
    } catch {
      this.error.set('Failed to request logs from devices');
    } finally {
      this.refreshingAll.set(false);
    }
  }

  async requestLogs(device: IBridgeDeviceDiagnosticStatus): Promise<void> {
    const current = this.requestingLogs();
    this.requestingLogs.set(new Set([...current, device.deviceId]));
    this._clearMessages();

    try {
      const practice = this.practice();
      const brandRef = Practice.brandDoc(practice);
      const organisationId = Firestore.getParentDocRef(brandRef).id;
      const command = this._buildGetLogsCommand();

      await this._sendCommand(
        command,
        device,
        practice,
        brandRef,
        organisationId
      );

      this.successMessage.set(`Logs requested from ${device.deviceName}`);
    } catch {
      this.error.set(`Failed to request logs from ${device.deviceName}`);
    } finally {
      const updated = new Set(this.requestingLogs());
      updated.delete(device.deviceId);
      this.requestingLogs.set(updated);
    }
  }

  copyDeviceId(device: IBridgeDeviceDiagnosticStatus): void {
    if (device.deviceId) {
      this._clipboard.copy(device.deviceId);
      this.successMessage.set('Device ID copied to clipboard');
    }
  }

  downloadBridge(device: IBridgeDeviceDiagnosticStatus): void {
    if (device.downloadUrl) {
      window.open(device.downloadUrl);
    }
  }

  async viewLogs(device: IBridgeDeviceDiagnosticStatus): Promise<void> {
    const deviceDoc = await Firestore.getDoc(device.deviceRef);
    this._dialog.open<ViewBridgeLogsDialogComponent, IViewBridgeLogsDialogData>(
      ViewBridgeLogsDialogComponent,
      DialogPresets.large({
        data: {
          device: deviceDoc,
          practice: this.practice(),
        },
      })
    );
  }

  async editDeviceName(device: IBridgeDeviceDiagnosticStatus): Promise<void> {
    const newName = await this._basicDialog.prompt({
      title: 'Edit Device Name',
      prompt: ['Enter a new name for this bridge device.'],
      defaultValue: device.deviceName,
    });

    if (!newName || newName === device.deviceName) {
      return;
    }

    try {
      await Firestore.patchDoc(device.deviceRef, { name: newName });
      this.successMessage.set(`Device renamed to "${newName}"`);
    } catch {
      this.error.set('Failed to rename device');
    }
  }

  async updateSettings(device: IBridgeDeviceDiagnosticStatus): Promise<void> {
    if (!device.hasSettings) {
      return;
    }

    const deviceDoc = await Firestore.getDoc(device.deviceRef);

    const dialogRef = this._dialog.open<
      EditBridgeSettingsDialogComponent,
      IEditBridgeSettingsDialogData,
      IEditBridgeSettingsDialogResult
    >(
      EditBridgeSettingsDialogComponent,
      DialogPresets.large({
        data: {
          settings: deviceDoc.settings,
        },
      })
    );

    const result = await dialogRef.afterClosed().toPromise();
    if (!result || !device.deviceId) {
      return;
    }

    try {
      const practice = this.practice();
      const brandRef = Practice.brandDoc(practice);
      const organisationId = Firestore.getParentDocRef(brandRef).id;
      await BridgeVersion.sendSettings(
        deviceDoc,
        BridgeDevice.getDeviceDatabaseRef(organisationId, device.deviceId),
        { deviceRef: device.deviceRef, practiceRef: practice.ref, brandRef },
        result.settings
      );
    } catch {
      this.error.set('Failed to send settings update');
    }
  }

  async generateIntegrationKey(
    device: IBridgeDeviceDiagnosticStatus
  ): Promise<void> {
    this.successMessage.set('Generating integration key...');

    try {
      const practice = this.practice();
      const result = await this._bridgeActions.generateIntegrationKey({
        deviceRef: device.deviceRef,
        deviceName: device.deviceName,
        deviceId: device.deviceId,
        hasIntegrationKey: device.hasIntegrationKey,
        practiceRef: practice.ref,
      });

      this.successMessage.set(undefined);
      if (!result) {
        return;
      }
    } catch {
      this.successMessage.set(undefined);
      this.error.set('Failed to generate integration key');
    }
  }

  async generateDownloadLink(
    device: IBridgeDeviceDiagnosticStatus
  ): Promise<void> {
    try {
      const practice = this.practice();
      await this._bridgeActions.generateDownloadLink({
        deviceRef: device.deviceRef,
        deviceName: device.deviceName,
        deviceId: device.deviceId,
        hasIntegrationKey: device.hasIntegrationKey,
        practiceRef: practice.ref,
      });
    } catch {
      this.error.set('Failed to generate download link');
    }
  }

  async deleteDevice(device: IBridgeDeviceDiagnosticStatus): Promise<void> {
    try {
      const deleted = await this._bridgeActions.deleteDevice(
        device.deviceRef,
        device.deviceName
      );
      if (deleted) {
        this.successMessage.set(`Device "${device.deviceName}" deleted`);
      }
    } catch {
      this.error.set(`Failed to delete "${device.deviceName}"`);
    }
  }

  async downloadDiagnosticReport(): Promise<void> {
    this.downloading.set(true);
    this._clearMessages();

    try {
      const practice = this.practice();
      const allDevices = await Firestore.getDocs(
        Practice.bridgeDeviceCol(practice)
      );

      const result = await buildBridgeDiagnosticReport(
        practice.name,
        practice.ref.path,
        allDevices
      );

      const practiceName = this._sanitiseFilename(practice.name);
      const dateStr = new Date().toISOString().slice(0, 10);
      const zipBlob = await result.zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${practiceName}-diagnostics-${dateStr}.zip`);

      this.successMessage.set(
        `Downloaded diagnostic report with ${result.totalLogFiles} log file${
          result.totalLogFiles === 1 ? '' : 's'
        }`
      );
    } catch {
      this.error.set('Failed to generate diagnostic report');
    } finally {
      this.downloading.set(false);
    }
  }

  private _observeDeviceStatuses(): void {
    this._practice$
      .pipe(
        switchMap((practice) => all$(Practice.bridgeDeviceCol(practice))),
        switchMap((devices) => this._observeDevicesWithFiles(devices)),
        map((devicesWithFiles) =>
          this._sortDeviceStatuses(
            devicesWithFiles.map((dwf) => this._toStatus(dwf))
          )
        ),
        catchError(() => {
          this.error.set('Failed to load bridge device statuses');
          return of([]);
        }),
        takeUntilDestroyed()
      )
      .subscribe((statuses) => {
        this.devices.set(statuses);
        this.loading.set(false);
      });
  }

  private _observeDevicesWithFiles(
    devices: WithRef<IBridgeDevice>[]
  ): Observable<IDeviceWithFiles[]> {
    if (devices.length === 0) {
      return of([]);
    }

    return combineLatest(
      devices.map((device) =>
        all$(BridgeDevice.fileCol(device)).pipe(
          map((files) => ({
            device,
            logFiles: files.filter(
              (file: WithRef<IAttachment>) => file.type === 'log'
            ),
          }))
        )
      )
    );
  }

  private _toStatus(dwf: IDeviceWithFiles): IBridgeDeviceDiagnosticStatus {
    const latestLog = dwf.logFiles.reduce<WithRef<IAttachment> | undefined>(
      (latest, file) =>
        !latest || file.lastModified > latest.lastModified ? file : latest,
      undefined
    );
    const latestLogDate = latestLog
      ? new Date(latestLog.lastModified)
      : undefined;

    return {
      deviceRef: dwf.device.ref,
      deviceName: dwf.device.name,
      deviceId: dwf.device.deviceId ?? '',
      status: dwf.device.status,
      deleted: dwf.device.deleted,
      downloadUrl: dwf.device.downloadUrl,
      version: dwf.device.settings?.version,
      hasSettings: !!dwf.device.settings,
      hasIntegrationKey: !!dwf.device.integrationKey,
      logCount: dwf.logFiles.length,
      latestLogDate,
      logFreshness: this._getLogFreshness(latestLogDate),
    };
  }

  private _sortDeviceStatuses(
    statuses: IBridgeDeviceDiagnosticStatus[]
  ): IBridgeDeviceDiagnosticStatus[] {
    return statuses.sort((a, b) => {
      if (a.deleted !== b.deleted) {
        return a.deleted ? 1 : -1;
      }
      return a.deviceName.localeCompare(b.deviceName);
    });
  }

  private _getLogFreshness(latestLogDate?: Date): BridgeLogFreshness {
    if (!latestLogDate) {
      return BridgeLogFreshness.None;
    }
    const today = new Date();
    const isToday =
      latestLogDate.getFullYear() === today.getFullYear() &&
      latestLogDate.getMonth() === today.getMonth() &&
      latestLogDate.getDate() === today.getDate();
    return isToday ? BridgeLogFreshness.Fresh : BridgeLogFreshness.Stale;
  }

  private _sanitiseFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
  }

  private _buildGetLogsCommand(): IBaseCommand<
    CloudToBridgeCommand.GetLogs,
    object
  > {
    return {
      type: CloudToBridgeCommand.GetLogs,
      data: {},
    };
  }

  private _clearMessages(): void {
    this.error.set(undefined);
    this.successMessage.set(undefined);
  }

  private async _sendCommand(
    command: IDeviceCommand['command'],
    device: IBridgeDeviceDiagnosticStatus,
    practice: WithRef<IPractice>,
    brandRef: IDeviceCommand['brandRef'],
    organisationId: string
  ): Promise<void> {
    if (!device.deviceId) {
      return;
    }

    const deviceCommand: IDeviceCommand = {
      deviceRef: device.deviceRef,
      practiceRef: practice.ref,
      brandRef,
      command,
    };

    const deviceDatabaseRef = BridgeDevice.getDeviceDatabaseRef(
      organisationId,
      device.deviceId,
      '/commands'
    );
    const commandRef = await push(deviceDatabaseRef);
    await set(commandRef, serialise(deviceCommand));
  }
}
